"use client";

import { c as _c } from "react/compiler-runtime";
import PaymentStep from "../payment-step";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaShoppingCart } from "react-icons/fa";
import { useParams } from "next/navigation";
import { EmbeddedFormPage } from "@/app/[tenant]/form/(form-page)/[formPath]/form-page-core";
import { Button } from "@/components/ui";
import { PatientFormFillProvider } from "@/contexts/form-fill-context";
import FormStateProvider from "@/contexts/form-provider";
import { usePurchaseAuth } from "@/contexts/purchase-auth-provider";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { MembershipShopCartLine, PurchaseBuyer, PurchaseCharges, ShopCartLine } from "@/types/shop";
import { ensureMembershipFormEnrollment } from "@/api/shop/shop";
const incompleteMembershipForm = (line: ShopCartLine): line is MembershipShopCartLine => line.itemType === ItemType.MEMBERSHIP && Boolean(line.form && !line.form.completed);
const mergeMembershipForm = (line: MembershipShopCartLine, catalogForm: MembershipShopCartLine["form"], optimisticallyCompleted: boolean): MembershipShopCartLine["form"] => {
  if (!catalogForm) return null;
  return {
    ...catalogForm,
    // Catalog data loaded before submission remains stale until its next
    // refetch. Preserve completion only in the checkout instance that submitted
    // the form; a persisted cart must accept authoritative expiry on remount.
    completed: catalogForm.completed || Boolean(optimisticallyCompleted && line.form?.id === catalogForm.id)
  };
};
interface CheckoutStageProps {
  lines: ShopCartLine[];
  totalCents: number;
  checkoutNonce?: string;
  pickupLocationId?: string | null;
  shippingBuyer?: PurchaseBuyer | null;
  fulfillmentMethod?: ShopFulfillmentMethod | null;
  onRemoveLine: (lineId: string) => void;
  onReplaceLine: (lineId: string, line: ShopCartLine) => void;
  onPaymentComplete: (totalCents: number, charges: PurchaseCharges) => void;
  onBack: () => void;
  onChangeLocation?: () => void;
  onChangeShippingAddress?: () => void;
  onChangeFulfillmentMethod?: () => void;
  onShippingUnavailable?: (message: string) => void;
}

/**
 * The "review + pay" stage: a back affordance to the browse stage plus the
 * Rainforest payment session, with a generic order summary built from the
 * cart's existing lines.
 */
const CheckoutStage: React.FC<CheckoutStageProps> = t0 => {
  const $ = _c(69);
  const {
    lines,
    totalCents,
    checkoutNonce,
    pickupLocationId,
    shippingBuyer,
    fulfillmentMethod,
    onRemoveLine,
    onReplaceLine,
    onPaymentComplete,
    onBack,
    onChangeLocation,
    onChangeShippingAddress,
    onChangeFulfillmentMethod,
    onShippingUnavailable
  } = t0;
  const {
    buyer,
    isAuthenticated,
    ensureAuthenticated
  } = usePurchaseAuth();
  const tenant = useParams()?.tenant;
  const {
    catalog
  } = useShopCatalog();
  const [optimisticallyCompletedLineIds, setOptimisticallyCompletedLineIds] = useState(_temp);
  const [enrollmentErrorMessage, setEnrollmentErrorMessage] = useState(null);
  let t1;
  let t2;
  if ($[0] !== catalog.items || $[1] !== lines || $[2] !== onReplaceLine || $[3] !== optimisticallyCompletedLineIds) {
    t1 = () => {
      for (const line of lines) {
        if (line.itemType !== ItemType.MEMBERSHIP) {
          continue;
        }
        const catalogMembership = catalog.items.find(item => item.type === ItemType.MEMBERSHIP && item.id === line.itemId);
        if (!catalogMembership) {
          continue;
        }
        const catalogForm = mergeMembershipForm(line, catalogMembership.form ?? null, optimisticallyCompletedLineIds.has(line.lineId));
        if (JSON.stringify(line.form) === JSON.stringify(catalogForm)) {
          continue;
        }
        onReplaceLine(line.lineId, {
          ...line,
          form: catalogForm,
          snapshot: {
            ...line.snapshot,
            membership: {
              ...line.snapshot.membership,
              form: catalogForm
            }
          }
        });
      }
    };
    t2 = [catalog.items, lines, onReplaceLine, optimisticallyCompletedLineIds];
    $[0] = catalog.items;
    $[1] = lines;
    $[2] = onReplaceLine;
    $[3] = optimisticallyCompletedLineIds;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== catalog.items || $[7] !== lines || $[8] !== optimisticallyCompletedLineIds) {
    let t4;
    if ($[10] !== catalog.items || $[11] !== optimisticallyCompletedLineIds) {
      t4 = line_0 => {
        if (line_0.itemType !== ItemType.MEMBERSHIP) {
          return line_0;
        }
        const catalogMembership_0 = catalog.items.find(item_0 => item_0.type === ItemType.MEMBERSHIP && item_0.id === line_0.itemId);
        return catalogMembership_0 ? {
          ...line_0,
          form: mergeMembershipForm(line_0, catalogMembership_0.form ?? null, optimisticallyCompletedLineIds.has(line_0.lineId))
        } : line_0;
      };
      $[10] = catalog.items;
      $[11] = optimisticallyCompletedLineIds;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = lines.map(t4);
    $[6] = catalog.items;
    $[7] = lines;
    $[8] = optimisticallyCompletedLineIds;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const effectiveLines = t3;
  let t4;
  if ($[13] !== effectiveLines) {
    t4 = effectiveLines.find(_temp2) ?? null;
    $[13] = effectiveLines;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const membershipFormLine = t4;
  let t5;
  if ($[15] !== effectiveLines) {
    t5 = effectiveLines.find(incompleteMembershipForm) ?? null;
    $[15] = effectiveLines;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const lineNeedingConsent = t5;
  const t6 = buyer?.id;
  const t7 = membershipFormLine?.itemId;
  let t8;
  if ($[17] !== pickupLocationId || $[18] !== t6 || $[19] !== t7 || $[20] !== tenant) {
    t8 = ["shop-membership-form-enrollment", tenant, t6, t7, pickupLocationId];
    $[17] = pickupLocationId;
    $[18] = t6;
    $[19] = t7;
    $[20] = tenant;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== buyer || $[23] !== membershipFormLine || $[24] !== pickupLocationId) {
    t9 = async () => {
      if (!buyer || !membershipFormLine) {
        return null;
      }
      const result = await ensureMembershipFormEnrollment({
        membershipId: membershipFormLine.itemId,
        pickupLocationId: pickupLocationId ?? undefined
      });
      if (!result) {
        throw new Error("Unable to prepare membership enrollment.");
      }
      return result;
    };
    $[22] = buyer;
    $[23] = membershipFormLine;
    $[24] = pickupLocationId;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = Boolean(isAuthenticated && buyer && membershipFormLine);
  let t11;
  if ($[26] !== t10 || $[27] !== t8 || $[28] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10,
      retry: false
    };
    $[26] = t10;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const enrollmentContext = useQuery(t11);
  let t12;
  let t13;
  if ($[30] !== enrollmentContext.error || $[31] !== enrollmentContext.isError) {
    t12 = () => {
      if (!enrollmentContext.isError) {
        setEnrollmentErrorMessage(null);
        return;
      }
      let active = true;
      parseError(enrollmentContext.error).then(message => {
        if (active) {
          setEnrollmentErrorMessage(message);
        }
      });
      return () => {
        active = false;
      };
    };
    t13 = [enrollmentContext.error, enrollmentContext.isError];
    $[30] = enrollmentContext.error;
    $[31] = enrollmentContext.isError;
    $[32] = t12;
    $[33] = t13;
  } else {
    t12 = $[32];
    t13 = $[33];
  }
  useEffect(t12, t13);
  let t14;
  if ($[34] !== onReplaceLine) {
    t14 = line_2 => {
      setOptimisticallyCompletedLineIds(completedLineIds => {
        const next = new Set(completedLineIds);
        next.add(line_2.lineId);
        return next;
      });
      onReplaceLine(line_2.lineId, {
        ...line_2,
        form: line_2.form ? {
          ...line_2.form,
          completed: true
        } : null,
        snapshot: {
          ...line_2.snapshot,
          membership: line_2.snapshot.membership ? {
            ...line_2.snapshot.membership,
            form: line_2.snapshot.membership.form ? {
              ...line_2.snapshot.membership.form,
              completed: true
            } : null
          } : undefined
        }
      });
    };
    $[34] = onReplaceLine;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const markConsentComplete = t14;
  const t15 = lineNeedingConsent ? "w-full max-w-7xl" : "max-w-xl";
  let t16;
  if ($[36] !== t15) {
    t16 = cx("motion-preset-slide-right-sm mx-auto", t15);
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FaArrowLeft className="mr-2 text-xxs" />;
    t18 = <span>Back</span>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t17 = $[38];
    t18 = $[39];
  }
  let t19;
  if ($[40] !== onBack) {
    t19 = <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 cursor-pointer px-0 text-foreground-secondary">{t17}{t18}</Button>;
    $[40] = onBack;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== buyer || $[43] !== checkoutNonce || $[44] !== enrollmentContext || $[45] !== enrollmentErrorMessage || $[46] !== ensureAuthenticated || $[47] !== fulfillmentMethod || $[48] !== isAuthenticated || $[49] !== lineNeedingConsent || $[50] !== lines || $[51] !== markConsentComplete || $[52] !== membershipFormLine || $[53] !== onBack || $[54] !== onChangeFulfillmentMethod || $[55] !== onChangeLocation || $[56] !== onChangeShippingAddress || $[57] !== onPaymentComplete || $[58] !== onRemoveLine || $[59] !== onReplaceLine || $[60] !== onShippingUnavailable || $[61] !== pickupLocationId || $[62] !== shippingBuyer || $[63] !== totalCents) {
    t20 = lines.length === 0 ? <section className="mx-auto flex max-w-md flex-col items-center py-16 text-center"><FaShoppingCart className="h-8 w-8 text-foreground-secondary" /><h2 className="mt-5 text-h3 text-foreground">Your cart is empty</h2><p className="mt-2 text-body leading-relaxed text-foreground-secondary">Add an item from the shop catalog to continue to checkout.</p><Button variant="primary" onClick={onBack} className="mt-6 cursor-pointer"><span>Back to shop</span></Button></section> : !lineNeedingConsent && isAuthenticated && buyer && membershipFormLine && enrollmentContext.isPending ? <section className="rounded-2xl border border-border bg-background p-5 shadow-sm"><p className="text-body text-foreground-secondary">Updating your membership checkout…</p></section> : !lineNeedingConsent && isAuthenticated && buyer && membershipFormLine && enrollmentContext.isError ? <section className="rounded-2xl border border-border bg-background p-5 shadow-sm"><p className="text-body text-status-error-text">{enrollmentErrorMessage ?? "Unable to prepare membership enrollment."}</p><Button variant="primary" onClick={() => enrollmentContext.refetch()} className="mt-3 cursor-pointer">Try again</Button></section> : lineNeedingConsent ? <section><p className="text-overline text-primary">Membership consent</p><h2 className="mt-2 text-h3 text-foreground"><span>{`Complete ${lineNeedingConsent.form?.name ?? "membership consent"}`}</span></h2><p className="mt-2 max-w-prose text-body text-foreground-secondary"><span>{`Consent is required before payment can be calculated for ${lineNeedingConsent.snapshot.name}.`}</span></p>{!isAuthenticated || !buyer ? <div className="mt-6 flex flex-col items-start gap-4 rounded-xl bg-background-secondary p-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-body-strong text-foreground">Sign in to continue</p><p className="mt-1 text-caption text-foreground-secondary">We need to verify it's you before loading this form.</p></div><Button variant="primary" onClick={() => ensureAuthenticated()} className="cursor-pointer">Sign in</Button></div> : enrollmentContext.isPending ? <div className="mt-6 rounded-xl bg-background-secondary p-4 text-body text-foreground-secondary">Preparing your membership form…</div> : enrollmentContext.isError ? <div className="mt-6 rounded-xl bg-status-error-background p-4"><p className="text-body text-status-error-text">{enrollmentErrorMessage ?? "Unable to prepare membership enrollment."}</p><Button variant="primary" onClick={() => enrollmentContext.refetch()} className="mt-3 cursor-pointer">Try again</Button></div> : <div className="mt-6 overflow-hidden"><FormStateProvider><PatientFormFillProvider membershipId={lineNeedingConsent.itemId}><EmbeddedFormPage formPath={lineNeedingConsent.form?.pathName ?? ""} patientId={buyer.id} onFormComplete={() => markConsentComplete(lineNeedingConsent)} skipCompletedCheck={true} /></PatientFormFillProvider></FormStateProvider></div>}</section> : <PaymentStep lines={lines} totalCents={totalCents} checkoutNonce={checkoutNonce} pickupLocationId={pickupLocationId} shippingBuyer={shippingBuyer} fulfillmentMethod={fulfillmentMethod} onRemoveLine={onRemoveLine} onReplaceLine={onReplaceLine} onPaymentComplete={onPaymentComplete} onChangeLocation={onChangeLocation} onChangeShippingAddress={onChangeShippingAddress} onChangeFulfillmentMethod={onChangeFulfillmentMethod} onShippingUnavailable={onShippingUnavailable} />;
    $[42] = buyer;
    $[43] = checkoutNonce;
    $[44] = enrollmentContext;
    $[45] = enrollmentErrorMessage;
    $[46] = ensureAuthenticated;
    $[47] = fulfillmentMethod;
    $[48] = isAuthenticated;
    $[49] = lineNeedingConsent;
    $[50] = lines;
    $[51] = markConsentComplete;
    $[52] = membershipFormLine;
    $[53] = onBack;
    $[54] = onChangeFulfillmentMethod;
    $[55] = onChangeLocation;
    $[56] = onChangeShippingAddress;
    $[57] = onPaymentComplete;
    $[58] = onRemoveLine;
    $[59] = onReplaceLine;
    $[60] = onShippingUnavailable;
    $[61] = pickupLocationId;
    $[62] = shippingBuyer;
    $[63] = totalCents;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== t16 || $[66] !== t19 || $[67] !== t20) {
    t21 = <div className={t16}>{t19}{t20}</div>;
    $[65] = t16;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  return t21;
};
export default CheckoutStage;
function _temp() {
  return new Set();
}
function _temp2(line_1) {
  return line_1.itemType === ItemType.MEMBERSHIP && Boolean(line_1.form);
}